import {computed} from "vue";
import {useI18n} from "vue-i18n";
import {configuration} from "@Helpers/configuration";
import { SupportedLocale } from "../../Plugins/localization";

export type WeekDays = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type WeekInfo = { firstDay: WeekDays, minimalDays: number, weekend: WeekDays[] };

export default function useCurrentLocale() {
    const i18n = useI18n();
    const currentLocale = computed(() => i18n.locale.value.substring(0, 2).toLowerCase());
    const urlWithLocale = computed(() => (url: string) => url.replace(':locale', currentLocale.value));
    const isoLocale = computed(() => i18n.locale.value as SupportedLocale);
    const weekInfo = computed<WeekInfo>(() => {
        const locale: Intl.Locale & {
            getWeekInfo?: () => WeekInfo,
            weekInfo?: WeekInfo
        } = new Intl.Locale(isoLocale.value);

        if (locale.getWeekInfo !== undefined) {
            return locale.getWeekInfo();
        }
        if (locale.weekInfo !== undefined) {
            return locale.weekInfo;
        }
        return {firstDay: 1, minimalDays: 4, weekend: [6, 7]};
    });

    // Format a number to a user locale
    const formatNumber = computed(() => (value: number) => {
        return isNaN(value) ? value : new Intl.NumberFormat(isoLocale.value).format(value);
    });

    // Format a number to a company currency
    const formatCurrency = computed(() => (
        value: number,
        options?: Intl.NumberFormatOptions
    ) => {
        return isNaN(value) ? value : new Intl.NumberFormat(isoLocale.value, {
            style: 'currency',
            currency: configuration.companyCurrency,
            ...options
        }).format(value);
    });

    return {
        currentLocale,
        urlWithLocale,
        isoLocale,
        i18n,
        weekInfo,
        formatNumber,
        formatCurrency
    };
};